import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
  ApolloProvider as OriginalApolloProvider,
} from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { Loader } from 'components/Forms';
import React, { FC, PropsWithChildren } from 'react';
import { useFirebase } from 'contexts/FirebaseContext';
import { Outlet } from 'react-router-dom';
import { CachePersistor } from 'apollo-cache-persist';
import { useConfig } from 'components/hooks';
import { onError } from '@apollo/link-error';
import * as Sentry from '@sentry/browser';
import authResolvers from './resolvers/auth.resolvers';

const cache = new InMemoryCache();

export const authCachePersistor = new CachePersistor({
  key: 'auth-apollo-cache',
  cache,
  debounce: 200,
  // @ts-ignore Check later again why this is not typed correctly
  storage: window.localStorage,
});

const AuthApolloProvider: FC<PropsWithChildren> = ({ children }) => {
  const config = useConfig();
  const { firebase } = useFirebase();

  const httpLink = createHttpLink({ uri: process.env.REACT_APP_GQL_AUTH_ENDPOINT });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) => {
        const error = `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`;
        Sentry.captureMessage(error);
        return console.error(error);
      });
    }

    if (networkError) console.error(`[Network error]: ${networkError}`);
  });

  // @ts-ignore
  const link = from([errorLink, httpLink]);

  const apolloClient = new ApolloClient({
    cache,
    resolvers: [authResolvers],
    link,
    connectToDevTools: process.env.NODE_ENV === 'development',
    defaultOptions: {
      // @ts-ignore This does not makes any sense here but it is still used somewhere
      firebase,
      config,
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      },
    },
  });

  if (process.env.NODE_ENV === 'development') {
    // Adds messages only in a dev environment
    loadDevMessages();
    loadErrorMessages();
  }

  if (!apolloClient) return <Loader />;

  return (
    <OriginalApolloProvider client={apolloClient}>
      <Outlet />
    </OriginalApolloProvider>
  );
};

export default AuthApolloProvider;
